'use client';
import Box from '@mui/material/Box';
import {styled} from '@mui/material/styles';
import type {NavContent, SiteBranding} from '@olympus-frontend/tenant-config';
import {useTranslation} from 'react-i18next';
import {trackHeaderRegisterInterestClick} from '../../analytics';
import {LOCALES, type Locale} from '../../shared/constants/locales';
import {isRtlLocale} from '../../shared/utils/locale';
import {RegisterInterestCta, useMobileRegisterInterestSuppressed} from '../register-interest';
import {BrandLogo} from './BrandLogo';
import {ContactLinks} from './ContactLinks';
import {DEVELOPMENTS_NAV_KEY, type DestinationsMenuData} from './DestinationsMenu';
import {LocaleSwitcher} from './LocaleSwitcher';
import {DestinationsAccordion, MobileMenu} from './MobileMenu';
import {NavItem} from './NavItem';

const MOBILE_LOGO_WIDTH = 88;
const MOBILE_LOGO_HEIGHT = 30;

const MOBILE_LOGO_HEIGHT_BY_LOCALE = {[LOCALES.ARABIC]: 'auto'} as const;

const MobileBar = styled(Box)(({theme}) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
  alignItems: 'center',
  paddingBlock: theme.spacing(1),
  minHeight: theme.spacing(7.5),
  [theme.breakpoints.up('md')]: {display: 'none'},
}));

const LeftCluster = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  justifySelf: 'start',
}));

const CenterCluster = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifySelf: 'center',
});

const RightCluster = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  justifySelf: 'end',
}));

interface HeaderMobileProps {
  branding: SiteBranding;
  content: NavContent;
  locale: Locale;
  locales: readonly Locale[];
  hotline?: string;
  whatsappHref?: string;
  developments?: DestinationsMenuData;
  showDevelopments: boolean;
}

export function HeaderMobile({
  branding,
  content,
  locale,
  locales,
  hotline,
  whatsappHref,
  developments,
  showDevelopments,
}: HeaderMobileProps) {
  const {t} = useTranslation();
  const suppressed = useMobileRegisterInterestSuppressed();
  return (
    <MobileBar>
      <LeftCluster>
        <ContactLinks hotline={hotline} whatsappHref={whatsappHref} />
      </LeftCluster>

      <CenterCluster>
        <BrandLogo
          logoUrl={branding.logoUrl}
          logoUrlByLocale={branding.logoUrlByLocale}
          alt={branding.siteName}
          width={MOBILE_LOGO_WIDTH}
          height={MOBILE_LOGO_HEIGHT}
          heightByLocale={MOBILE_LOGO_HEIGHT_BY_LOCALE}
        />
      </CenterCluster>

      <RightCluster>
        <LocaleSwitcher current={locale} locales={locales} />
        <MobileMenu
          anchor={isRtlLocale(locale) ? 'left' : 'right'}
          cta={suppressed ? undefined : <RegisterInterestCta fullWidth onClick={trackHeaderRegisterInterestClick} />}
          logoSlot={
            <BrandLogo
              logoUrl={branding.logoUrl}
              logoUrlByLocale={branding.logoUrlByLocale}
              alt={branding.siteName}
              width={MOBILE_LOGO_WIDTH}
              height={MOBILE_LOGO_HEIGHT}
              heightByLocale={MOBILE_LOGO_HEIGHT_BY_LOCALE}
            />
          }
        >
          {content.links.map(link => {
            if (link.key === DEVELOPMENTS_NAV_KEY && showDevelopments && developments) {
              return (
                <DestinationsAccordion
                  key={link.key}
                  labelKey={link.labelKey}
                  countries={developments.countries}
                  projects={developments.projects}
                  countriesViewAllHref={developments.countriesViewAllHref}
                  projectsViewAllHref={developments.projectsViewAllHref}
                  t={t}
                />
              );
            }
            return <NavItem key={link.key} item={link} type="mobile" />;
          })}
        </MobileMenu>
      </RightCluster>
    </MobileBar>
  );
}

HeaderMobile.displayName = 'HeaderMobile';
